// @ts-strict-ignore
/**
 * This module provides utilities for tracking the timing of UI "workflows" to Mixpanel.
 *
 * A workflow is indented to capture the time between the invocation and the time at which its predicate function evaluates to true.
 * Predicate functions are run anytime a `trackinfra` event bubbles up to the window. The assumption being, any events that can trigger
 * a workflow to complete is probably a general metric we're interested in.
 * Workflows can be preempted by the calling component being disconnected, or by the creation of a new workflow with the same key.
 *
 * Workflow preemption and completion will all be logged to the infra metrics project via emitInfraTrackEvent
 *
 * For example, if you want to track time time between user clicking a button and an API call completing. You would start the workflow
 * in the click handler by calling captureWorkflowTiming(), and it would wait for a `trackinfra` event to be emitted in the API call
 * completion. This will allow for isolated timing metrics around the API call specifically, as well as typical user workflow timing
 * starting from the time the button was clicked and the ending when the API call finished.
 */
import isNil from 'lodash/isNil';

import {getProjectId, getReportName, getWorkspaceId} from 'common/report/util';
import {emitInfraTrackEvent, getCustomElementParents} from 'common/util/dom';
import {PerfMeasure} from 'common/report/perf-measure';

import {diffFromNow, getNow, PERFORMANCE_EVENT_PREFIX} from './timing';

export enum LoadingType {
  Table = `table`,
  Spinner = `spinner`,
  Skeleton = `skeleton`,
}

export const QueryStartEvent = `[${PERFORMANCE_EVENT_PREFIX}] Query Start`;
export enum Workflow {
  /**
   * This is the time taken for parsing and issuing a query. Can be both a saved bookmark, saved bookmark
   * with overrides, or an ad-hoc bookmark
   */
  BookmarkToQuery = `bookmark-to-query`,
}
export enum BookmarkState {
  Saved = `saved`,
  AdHoc = `ad-hoc`,
  BookmarkUrlSlug = `bookmark-url-slug`,
  SavedWithOverrides = `saved with overrides`,
  Invalid = `invalid`,
}

function getCommonProps() {
  return {
    now: getNow(),
    distinct_id: getProjectId(),
    project_id: getProjectId(),
    workspace_id: getWorkspaceId() || undefined,
    report: getReportName(),
  };
}

type ListenerOptions = {
  /** Event to listen for and call the selector function  */
  eventName: string;
  /** The object to attach the listener to, assumed to be the window if not provided */
  listenOn?: Element | Window | Document;
};
type CallbackOptions = {
  /** a function to run to determine if the callback should fire */
  isDone: (ev: CustomEvent) => boolean;
  /** Function to call with the total elapsed time and the event that triggered completion */
  done: (args: {elapsedMs: number; ev: CustomEvent}) => void;
};
/**
 * Generic function to provide timing around events that are expected to happen across component tree's but
 * share a common ancestor (can be the window)
 *
 * @example
 * timeUntilEvent(
 *  {eventName: "mousemove"},
 *  {isDone: (ev) => ev.clientX > 200,
 *  done:({elapsedMs}) => console.log(`The user took ${elapsedMs} to move their mouse past the header`)
 * })
 *
 * @returns a function that will return the timestamp at which it started listening at if done was
 *  never fired, or null if done was called. This function will also cleanup any outstanding event listeners
 */
function timeUntilEvent({eventName, listenOn}: ListenerOptions, {isDone, done}: CallbackOptions): () => null | number {
  const startedAt = getNow();
  let hasCompleted = false;

  const listener = (ev: CustomEvent) => {
    if (!hasCompleted && isDone(ev)) {
      hasCompleted = true;
      done({elapsedMs: diffFromNow(startedAt), ev});
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      setTimeout(cleanup, 0);
    }
  };

  const cleanup = () => {
    (listenOn ?? window).removeEventListener(eventName, listener);
  };

  (listenOn ?? window).addEventListener(eventName, listener);
  return () => {
    if (!hasCompleted) {
      cleanup();
      return startedAt;
    }
    return null;
  };
}

/**
 * Predicate function to determine if the workflow is completed. The function is passed the event that triggered the callback
 * as well as the component that triggered the workflow.
 */
export type WorkflowComplete<T> = (ev: CustomEvent, component: T) => boolean;
type WorkflowAdditionalProps = Record<string, string | number | boolean>;
export type WorkflowOptions = {
  /**
   * The workflow event name, will appear as the event name, and the interruptedBy field if
   * a running workflow is stopped.
   */
  name?: string;
  /** Additional properties for the track event to fire on workflow complete */
  additionalProps?: WorkflowAdditionalProps | (() => WorkflowAdditionalProps);
  /** The unique key that is used to find and cancel any already running workflow for the component */
  key: string;
  /** This is the predicate function that determines if the workflow is complete */
  isComplete: (ev: CustomEvent, component) => boolean;
  // Used by unit tests to avoid using rAF as it complicates the testing
  avoidRaf?: boolean;
};
type ExistingWorkflow = {
  options: WorkflowOptions;
  /**
   * Cleans up an existing workflow by removing any existing listeners. If the workflow completed it will return null,
   * if it did not complete, it will return the timestamp when the workflow started.
   */
  cleanup: () => null | number;
};

function nextTick(callback: () => any) {
  // NOTE: on Node 16, MessageChannel is available globally
  // Usage of messageChannel will break some testcases (sinon.js won't mock it by default)
  // we define this `window.MessageChannelForTiming` as an alias to `window.MessageChannel`
  // This alias is only defined in browser env, so in node.js env it will always fallback to setTimeout
  // see polyfill/index.js
  if (typeof MessageChannelForTiming === `function`) {
    // We use MessageChannel instead of setTimeout because it has higher priority. See
    // https://www.webperf.tips/tip/measuring-paint-time/ for more details.
    // We fallback to setTimeout on unit testing node environment doesn't have access MessageChannel.
    const messageChannel = new MessageChannelForTiming();
    messageChannel.port1.onmessage = callback;
    messageChannel.port2.postMessage(undefined);
  } else {
    setTimeout(callback, 0);
  }
}

/**
 * Measure the time required for a workflow to complete. Each workflow must have a key and a complete function that indicates
 * the workflow should be marked finished. Multiple calls with the same key will interrupt and restart the workflow if it was
 * not completed. If a workflow is running and the originating component is disconnected, it will be captured.
 *
 * A workflow is assumed to be driven off of `trackinfra` events which can be listened to from the window.
 *
 * The total time is assumed to rely on multiple factors (determined by the complete function) and triggered complete by a `trackinfra` call.
 *
 * See https://github.com/mixpanel/analytics/pull/37635 for the original use.
 */
export function captureWorkflowTiming(component, options: WorkflowOptions) {
  component.pendingWorkflow = component.pendingWorkflow ?? new Map<string, ExistingWorkflow>();

  // find the existing workflow, it will be cancelled if still running and removed from the pendingWorkflow map
  cancelWorkflow(component, options.key, `interrupted`, options.name);

  const startedAt = getNow();
  const cleanUpListener = timeUntilEvent(
    {eventName: `trackinfra`},
    {
      isDone: (ev) => options.isComplete(ev, component),
      done: ({elapsedMs}) => {
        let elapsedMsPostTick: number = null;
        nextTick(() => {
          elapsedMsPostTick = diffFromNow(startedAt);
        });

        const callback = () => {
          nextTick(() => {
            const elapsedMsPostPaint = diffFromNow(startedAt);

            const additionalProps =
              typeof options.additionalProps === `function` ? options.additionalProps() : options.additionalProps;
            emitInfraTrackEvent(window, {
              eventName: options.name || `Workflow`,
              eventPrefix: PERFORMANCE_EVENT_PREFIX,
              props: {
                elapsedMs,
                // Waiting a tick when captureWorkflowTiming is called from inside a rAF will account for the time
                // until DOM updates are painted to screen
                elapsedMsPostTick,
                // Waiting a rAF + tick is needed to accurately capture the time it takes to pain certain DOM updates
                // to the screen (e.g. properties menu)
                // TODO: For the properties menu render/DOM update, the call to captureWorkflowTiming itself
                // occurs inside a rAF (i.e. the rAF to re-render after Component.update) which means waiting a
                // rAF + tick (i.e. elapsedMsPostPaint) will actually occur after two paints. It's only after this
                // 2nd paint that the properties menu is painted to the screen (even though the DOM insertion happens
                // during a rAF before the 1st paint). (These findings were observed when using the Chrome Performance
                // profiler.) This is unexpected and might be worth digging into.
                elapsedMsPostPaint,
                key: options.key,
                workflowStatus: `completed`,
                ...getCommonProps(),
                ...additionalProps,
              },
            });
            component.pendingWorkflow.delete(options.key);
          });
        };
        if (options.avoidRaf) {
          callback();
        } else {
          requestAnimationFrame(callback);
        }
      },
    },
  );

  // Save this workflow's cleanup function and "metadata" so that it can be cleaned up later if necessary
  const workflow: ExistingWorkflow = {
    options,
    cleanup: cleanUpListener,
  };
  component.pendingWorkflow.set(options.key, workflow);

  // ensure if the workflow is preempted due to component disconnect, that it is captured,
  // if the workflow has already completed, this is a noop
  component.onDisconnected(() => cancelWorkflow(component, options.key, `disconnected`));
}

export function cancelWorkflow(component, workflowKey: string, reason: string, interruptedBy?: string) {
  const workflowObject = component.pendingWorkflow.get(workflowKey);
  if (workflowObject) {
    // existing workflow object was found, attempting to call the cleanup function, it will return null if the existing
    // workflow completed and cleaned up successfully
    const pendingWorkflowStartedAt = workflowObject.cleanup();
    if (!isNil(pendingWorkflowStartedAt)) {
      let additionalProps;

      try {
        additionalProps =
          typeof workflowObject.options.additionalProps === `function`
            ? workflowObject.options.additionalProps()
            : workflowObject.options.additionalProps;
      } catch (err) {
        console.error(err);
        additionalProps = {};
      }

      emitInfraTrackEvent(window, {
        eventName: workflowObject.options.name || `Workflow`,
        eventPrefix: PERFORMANCE_EVENT_PREFIX,
        props: {
          elapsedMs: diffFromNow(pendingWorkflowStartedAt),
          key: workflowObject.options.key,
          workflowStatus: reason,
          ...(interruptedBy ? {interruptedBy} : undefined),
          ...getCommonProps(),
          ...additionalProps,
        },
      });
    }
    component.pendingWorkflow.delete(workflowKey);
  }
}

/**
 * Extracts the time the custom event was created and calculates the difference from now.
 *
 * Useful to understand and track how long it takes for events to cross boundaries and result in desired handling
 */
export function timeToHandleEvent(ev: CustomEvent<any>): void {
  const target = ev.target.toString();
  const parents = getCustomElementParents(ev.target as Element);
  const elapsedMs = diffFromNow(ev.timeStamp);
  emitInfraTrackEvent(window, {
    eventName: `Time to Handle Event`,
    eventPrefix: PERFORMANCE_EVENT_PREFIX,
    props: {
      eventName: ev.type,
      sourceComponent: target.includes(`#`) ? target.substring(0, target.indexOf(`#`)) : target,
      parentPath: parents.join(`>`),
      elapsedMs,
      parents,
      ...getCommonProps(),
    },
  });
}

/**
 * Convenience function for tracking event handling. See `timeToHandleEvent`
 *
 * @example
 * on={
 *  select: instrumentWithTimer(this.helpers.handleSelect),
 * }
 */
export function instrumentWithTimer(eventHandler: (ev: any) => void): (ev: any) => void {
  return (ev: any) => {
    timeToHandleEvent(ev);
    return eventHandler(ev);
  };
}

/**
 * Convenience function for tracking a measurement from the Performance API
 *
 * @example
 * const myMeasure = new PerfMeasure(`my-timing`);
 * myMeasure.start();
 * doSomething();
 * myMeasure.end();
 * trackPerfDuration(`My Timing`, myMeasure);
 *
 */
export function trackPerfDuration(eventName: string, measurer: PerfMeasure, extraProps?: Record<string, any>): void {
  if (Number.isFinite(measurer.duration)) {
    emitInfraTrackEvent(window, {
      eventName,
      eventPrefix: PERFORMANCE_EVENT_PREFIX,
      props: {
        elapsedMs: measurer.duration,
        ...getCommonProps(),
        ...extraProps,
      },
    });
  }
}
